/**
 * SAEweb API Service
 * Centralizes all API calls to the Django backend.
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Get CSRF token from cookies
function getCsrfToken() {
  const match = document.cookie.match(/csrftoken=([^;]+)/);
  return match ? match[1] : '';
}

// Add CSRF token to mutating requests
api.interceptors.request.use((config) => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = getCsrfToken();
  }
  return config;
});

// ─── AUTH ──────────────────────────────────────
export const auth = {
  check: () => api.get('/auth/check/'),
  login: (username, password) => api.post('/auth/login/', { username, password }),
  register: (data) => api.post('/auth/register/', data),
  logout: () => api.post('/auth/logout/'),
  profile: () => api.get('/auth/profile/'),
  changePassword: (data) => api.post('/auth/change-password/', data),
  requestReset: (email) => api.post('/auth/request-reset/', { email }),
  resetConfirm: (data) => api.post('/auth/reset-confirm/', data),
};

// ─── PUBLIC ───────────────────────────────────
export const publicApi = {
  getSettings: () => api.get('/public/settings/'),
  getLinks: () => api.get('/public/links/'),
  getRestaurants: () => api.get('/public/restaurants/'),
  getContracts: () => api.get('/public/contracts/'),
  getOnboardingDocs: (token) => api.get('/public/onboarding-documents/', { params: token ? { token } : {} }),
  submitVacation: (data) => api.post('/public/vacation-request/', data),
  // Onboarding (token-based)
  verifyOnboarding: (token, accessCode) => api.post(`/public/onboarding/${token}/verify/`, { access_code: accessCode }),
  updateOnboarding: (token, data) => {
    if (data instanceof FormData) {
      return api.patch(`/public/onboarding/${token}/update/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.patch(`/public/onboarding/${token}/update/`, data);
  },
};

// ─── ADMIN ────────────────────────────────────
export const adminApi = {
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard/'),

  // Settings
  getSettings: () => api.get('/admin/settings/'),
  updateSettings: (data) => api.post('/admin/settings/', data),

  // Restaurants
  getRestaurants: () => api.get('/admin/restaurants/'),
  createRestaurant: (data) => api.post('/admin/restaurants/', data),
  updateRestaurant: (id, data) => api.patch(`/admin/restaurants/${id}/`, data),
  deleteRestaurant: (id) => api.delete(`/admin/restaurants/${id}/`),

  // Categories
  getCategories: () => api.get('/admin/categories/'),
  createCategory: (data) => api.post('/admin/categories/', data),

  // Employees
  getEmployees: (params) => api.get('/admin/employees/', { params }),
  getEmployee: (id) => api.get(`/admin/employees/${id}/`),
  createEmployee: (data) => api.post('/admin/employees/', data),
  updateEmployee: (id, data) => api.patch(`/admin/employees/${id}/`, data),
  transferEmployee: (id, data) => api.patch(`/admin/employees/${id}/transfer/`, data),
  setEmployeeCredentials: (id, data) => api.post(`/admin/employees/${id}/set_credentials/`, data),
  getEmployeeDocuments: (id) => api.get(`/admin/employees/${id}/documents/`),
  deleteEmployee: (id) => api.delete(`/admin/employees/${id}/`),
  // Borrado definitivo, solo para superusuarios: elimina también las
  // solicitudes de alta del empleado y sus archivos del disco.
  purgeEmployee: (id) => api.delete(`/admin/employees/${id}/purge/`, { data: { confirm: true } }),
  purgeSubmission: (id) => api.delete(`/admin/new-employee-submissions/${id}/purge/`, { data: { confirm: true } }),
  // El .zip se descarga como blob: va por la API, que exige sesión, así que
  // no basta con un enlace normal.
  downloadSubmissionZip: (id) => api.get(`/admin/new-employee-submissions/${id}/descargar/`, { responseType: 'blob' }),
  setEmployeeActive: (id, isActive) => api.patch(`/admin/employees/${id}/set_active/`, { is_active: isActive }),

  // Courses
  getCourses: () => api.get('/admin/courses/'),
  createCourse: (data) => api.post('/admin/courses/', data),
  updateCourse: (id, data) => api.patch(`/admin/courses/${id}/`, data),
  deleteCourse: (id) => api.delete(`/admin/courses/${id}/`),

  // Course Assignments
  getAssignments: (params) => api.get('/admin/course-assignments/', { params }),
  createAssignment: (data) => api.post('/admin/course-assignments/', data),
  updateAssignment: (id, data) => api.patch(`/admin/course-assignments/${id}/`, data),
  deleteAssignment: (id) => api.delete(`/admin/course-assignments/${id}/`),

  // Course Completions
  getCompletions: (params) => api.get('/admin/course-completions/', { params }),
  createCompletion: (data) => api.post('/admin/course-completions/', data),

  // Vacation Requests
  getVacationRequests: (params) => api.get('/admin/vacation-requests/', { params }),
  updateVacationRequest: (id, data) => api.patch(`/admin/vacation-requests/${id}/`, data),

  // New Employee Submissions
  getSubmissions: (params) => api.get('/admin/new-employee-submissions/', { params }),
  approveSubmission: (id, data) => api.post(`/admin/new-employee-submissions/${id}/approve/`, data),
  updateSubmission: (id, data) => api.patch(`/admin/new-employee-submissions/${id}/`, data),
  // Reenvío de la bienvenida: admite `email` y `regenerar_contrasena`.
  reenviarBienvenida: (id, data) => api.post(`/admin/new-employee-submissions/${id}/reenviar/`, data),
  // Historial de envíos de correo (filtros: submission, estado, tipo).
  getEnvios: (params) => api.get('/admin/envios/', { params }),
  // Reenvío del correo del curso de PRL (solo con los exámenes aprobados).
  reenviarPrl: (id) => api.post(`/admin/new-employee-submissions/${id}/reenviar-prl/`),
  // Plantillas de correo (clave: 'bienvenida' o 'prl') y ajustes del correo.
  getPlantilla: (clave) => api.get(`/admin/plantillas/${clave}/`),
  guardarPlantilla: (clave, data) => api.put(`/admin/plantillas/${clave}/`, data),
  vistaPreviaPlantilla: (clave, data) => api.post(`/admin/plantillas/${clave}/vista-previa/`, data),
  enviarPruebaPlantilla: (clave, email) => api.post(`/admin/plantillas/${clave}/prueba/`, { email }),
  getAjustesCorreo: () => api.get('/admin/correo/ajustes/'),
  updateAjustesCorreo: (data) => api.patch('/admin/correo/ajustes/', data),

  // Onboarding invitations
  createOnboarding: (data) => api.post('/admin/onboarding/create/', data),
  // Alta masiva: sin `confirmar` solo comprueba el archivo; con él, crea las válidas.
  importarAltas: (archivo, confirmar, fechaOrientacion) => {
    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('fecha_orientacion', fechaOrientacion || '');
    if (confirmar) formData.append('confirmar', 'true');
    return api.post('/admin/onboarding/importar/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Contracts
  getContracts: () => api.get('/admin/contracts/'),
  uploadContract: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.post('/admin/contracts/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteContract: (id) => api.delete(`/admin/contracts/${id}/`),

  // Onboarding Documents
  getOnboardingDocs: () => api.get('/admin/onboarding-documents/'),
  uploadOnboardingDoc: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.post('/admin/onboarding-documents/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateOnboardingDoc: (id, data) => api.patch(`/admin/onboarding-documents/${id}/`, data),
  deleteOnboardingDoc: (id) => api.delete(`/admin/onboarding-documents/${id}/`),

  // External Links
  getLinks: () => api.get('/admin/external-links/'),
  createLink: (data) => api.post('/admin/external-links/', data),
  updateLink: (id, data) => api.patch(`/admin/external-links/${id}/`, data),
  deleteLink: (id) => api.delete(`/admin/external-links/${id}/`),

  // Excel exports (returns blob)
  exportEmployees: (params) => api.get('/admin/export/employees/', { params, responseType: 'blob' }),
  exportVacations: (params) => api.get('/admin/export/vacations/', { params, responseType: 'blob' }),

  // Employee course detail
  getEmployeeCourses: (employeeId) => api.get(`/admin/employees/${employeeId}/courses/`),
};

// ─── Gestoría API ────────────────────────────
export const gestoriaApi = {
  getSubmissions: (params) => api.get('/gestoria/submissions/', { params }),
  uploadDocs: (id, formData) => api.post(`/gestoria/submissions/${id}/upload/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export default api;
